import { useState, useMemo, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSessionState } from "@/hooks/useSessionState";
import { format } from "date-fns";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  Filter,
  Download,
  Users,
  CreditCard,
  TrendingUp,
  AlertCircle,
  Calendar,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  Percent,
  Gift,
  X,
  Tag,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

import {
  fetchUserSubscriptionsOverview,
  fetchUserSubscriptionsFiltered,
} from "@/api/user-subscriptions";
import { fetchSubscriptionPlans } from "@/api/subscription-plans";
import { usePagination } from "@/hooks/usePagination";
import { useDebounce } from "@/hooks/useDebounce";
import AdminLayout from "@/components/layout/AdminLayout";
import type { UserSubscription } from "@/types/subscription";

type SubscriptionStatus =
  | "active"
  | "cancelled"
  | "expired"
  | "pending"
  | "all";

const UserSubscriptionsList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useSessionState("userSubscriptionsList.searchTerm", "");
  const [statusFilter, setStatusFilter] = useSessionState<SubscriptionStatus>("userSubscriptionsList.statusFilter", "all");
  const [planFilter, setPlanFilter] = useSessionState("userSubscriptionsList.planFilter", "all");
  const [currentPage, setCurrentPage] = useSessionState("userSubscriptionsList.currentPage", 1);
  const [pageSize, setPageSize] = useSessionState("userSubscriptionsList.pageSize", 10);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Fetch filtered subscriptions from DB (server-side filtering + pagination)
  const {
    data: filteredData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "userSubscriptionsFiltered",
      statusFilter,
      planFilter,
      currentPage,
      pageSize,
      debouncedSearchTerm,
    ],
    queryFn: async () => {
      const planParam = planFilter === "all" ? "all" : Number(planFilter);
      return fetchUserSubscriptionsFiltered({
        status: statusFilter,
        planId: planParam,
        page: currentPage,
        pageSize,
        search: debouncedSearchTerm,
      });
    },
  });

  // Fetch user subscriptions overview/stats
  const { data: subscriptionsOverview } = useQuery({
    queryKey: ["userSubscriptionsOverview"],
    queryFn: fetchUserSubscriptionsOverview,
  });

  // Fetch subscription plans for filtering
  const { data: subscriptionPlans = [], isLoading: isLoadingPlans } = useQuery({
    queryKey: ["subscriptionPlans"],
    queryFn: fetchSubscriptionPlans,
  });

  // Apply client-side search on the current page items from server
  const pageItems = useMemo(() => {
    const items = filteredData?.items || [];
    if (!debouncedSearchTerm) return items;
    const term = debouncedSearchTerm.toLowerCase();
    return items.filter((subscription) => {
      return (
        subscription.user?.name?.toLowerCase().includes(term) ||
        subscription.user?.email?.toLowerCase().includes(term)
      );
    });
  }, [filteredData, debouncedSearchTerm]);

  // Calculate pagination using server total
  const totalItems = filteredData?.total || 0;
  const { totalPages, canGoPrev, canGoNext, pageNumbers } = usePagination({
    totalItems,
    itemsPerPage: pageSize,
    currentPage,
  });

  // Reset to first page when filters change
  const resetPage = () => setCurrentPage(1);

  // Reset filters function
  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setPlanFilter("all");
    setCurrentPage(1);
  };

  // Skip page reset on first render (preserves restored page from sessionStorage)
  const isFirstRender = useRef(true);

  // Reset to first page when filters change
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setCurrentPage(1);
  }, [statusFilter, planFilter, debouncedSearchTerm]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-500 hover:bg-green-600";
      case "cancelled":
        return "bg-yellow-500 hover:bg-yellow-600";
      case "expired":
        return "bg-gray-500 hover:bg-gray-600";
      case "pending":
        return "bg-blue-500 hover:bg-blue-600";
      default:
        return "bg-gray-500 hover:bg-gray-600";
    }
  };

  const computeStatus = (subscription: UserSubscription): string => {
    const now = new Date();
    const start = subscription.subscriptionStart
      ? new Date(subscription.subscriptionStart)
      : null;
    const end = subscription.subscriptionEnd
      ? new Date(subscription.subscriptionEnd)
      : null;

    // Preserve cancelled as-is
    if (subscription.status === "cancelled") return "cancelled";

    if (end && end <= now) return "expired";
    if (
      subscription.status === "active" &&
      !!start &&
      start <= now &&
      (!end || end > now)
    ) {
      return "active";
    }
    return subscription.status || "pending";
  };

  const formatStatusText = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const handleExport = () => {
    // TODO: Implement CSV export functionality
    toast.info("Export functionality will be implemented soon");
  };

  if (error) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Error Loading Subscriptions
            </h3>
            <p className="text-muted-foreground">
              Failed to load user subscriptions. Please try again.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout title="User Subscriptions">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">User Subscriptions</h1>
            <p className="text-muted-foreground mt-1">
              Manage and monitor all user subscriptions
            </p>
          </div>
          <Button onClick={handleExport} variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Row 1 */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Subscriptions
              </CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {subscriptionsOverview?.total || 0}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active</CardTitle>
              <TrendingUp className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {subscriptionsOverview?.active || 0}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Cancelled</CardTitle>
              <AlertCircle className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">
                {subscriptionsOverview?.cancelled || 0}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Expired</CardTitle>
              <Calendar className="h-4 w-4 text-gray-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-600">
                {subscriptionsOverview?.expired || 0}
              </div>
            </CardContent>
          </Card>

          {/* Row 2 */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Revenue (excl. cancelled)
              </CardTitle>
              <DollarSign className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                ₹{(subscriptionsOverview?.totalRevenueINR || 0).toFixed(2)}
              </div>
              {typeof subscriptionsOverview?.totalRevenueUSD === "number" && (
                <div className="text-sm text-muted-foreground mt-1">
                  ${subscriptionsOverview.totalRevenueUSD.toFixed(2)}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Promo Discounts
              </CardTitle>
              <Tag className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                ₹{(subscriptionsOverview?.totalPromoDiscounts || 0).toFixed(2)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Referral Discounts
              </CardTitle>
              <Gift className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                ₹
                {(subscriptionsOverview?.totalReferralDiscounts || 0).toFixed(
                  2
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Discounts
              </CardTitle>
              <Percent className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                ₹{(subscriptionsOverview?.totalDiscounts || 0).toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground">
                {subscriptionsOverview?.subscriptionsWithDiscounts || 0}{" "}
                subscriptions
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Search by user name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <Select
                value={statusFilter}
                onValueChange={(value: SubscriptionStatus) =>
                  setStatusFilter(value)
                }
              >
                <SelectTrigger className="w-full md:w-[180px]">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>

              <Select value={planFilter} onValueChange={setPlanFilter}>
                <SelectTrigger className="w-full md:w-[200px]">
                  <CreditCard className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Filter by plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Plans</SelectItem>
                  {subscriptionPlans && subscriptionPlans.length > 0 ? (
                    subscriptionPlans.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id.toString()}>
                        {plan.name}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="loading" disabled>
                      {isLoadingPlans
                        ? "Loading plans..."
                        : "No plans available"}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>

              {/* Reset Filters Button */}
              {(searchTerm ||
                statusFilter !== "all" ||
                planFilter !== "all") && (
                <Button
                  variant="outline"
                  onClick={resetFilters}
                  className="flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  Reset
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Subscriptions Table */}
        <Card>
          <CardHeader>
            <CardTitle>Subscriptions ({totalItems})</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 10 }).map((_, i) => (
                  <div key={i} className="flex items-center space-x-4">
                    <Skeleton className="h-4 w-[200px]" />
                    <Skeleton className="h-4 w-[150px]" />
                    <Skeleton className="h-4 w-[100px]" />
                    <Skeleton className="h-4 w-[120px]" />
                    <Skeleton className="h-4 w-[80px]" />
                  </div>
                ))}
              </div>
            ) : pageItems.length === 0 ? (
              <div className="text-center py-8">
                <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  No Subscriptions Found
                </h3>
                <p className="text-muted-foreground">
                  {searchTerm || statusFilter !== "all" || planFilter !== "all"
                    ? "No subscriptions match your current filters."
                    : "No user subscriptions have been created yet."}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Discounts</TableHead>
                      <TableHead>Start Date</TableHead>
                      <TableHead>End Date</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Payment Method</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pageItems.map((subscription) => (
                      <TableRow
                        key={subscription.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() =>
                          navigate(
                            `/admin/user-subscriptions/${subscription.id}`
                          )
                        }
                      >
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {subscription.user?.name || "Unknown User"}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {subscription.user?.email}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">
                            {subscription.subscriptionPlan?.name ||
                              "Unknown Plan"}
                          </span>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const status = computeStatus(subscription);
                            return (
                              <Badge className={getStatusColor(status)}>
                                {formatStatusText(status)}
                              </Badge>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const finalPrice = subscription.price || 0;
                            const discount = subscription.totalDiscount || 0;
                            const base = Math.max(0, finalPrice + discount);
                            return (
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  ₹{finalPrice.toFixed(2)}{" "}
                                </span>
                                {discount > 0 && (
                                  <span className="text-xs text-muted-foreground line-through">
                                    ₹{base.toFixed(2)}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {subscription.totalDiscount &&
                            subscription.totalDiscount > 0 ? (
                              <>
                                <div className="text-sm font-medium text-green-600">
                                  -₹{subscription.totalDiscount.toFixed(2)}{" "}
                                  total
                                </div>
                                {((subscription.promoDiscount &&
                                  subscription.promoDiscount > 0) ||
                                  (subscription.referralDiscount &&
                                    subscription.referralDiscount > 0)) && (
                                  <div className="text-xs text-muted-foreground space-y-1">
                                    {subscription.promoDiscount &&
                                      subscription.promoDiscount > 0 && (
                                        <div>
                                          Promo: -₹
                                          {subscription.promoDiscount.toFixed(
                                            2
                                          )}
                                        </div>
                                      )}
                                    {subscription.referralDiscount &&
                                      subscription.referralDiscount > 0 && (
                                        <div>
                                          Referral: -₹
                                          {subscription.referralDiscount.toFixed(
                                            2
                                          )}
                                        </div>
                                      )}
                                  </div>
                                )}
                              </>
                            ) : (
                              <span className="text-sm text-muted-foreground">
                                No discount
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {format(
                            new Date(subscription.subscriptionStart),
                            "MMM dd, yyyy"
                          )}
                        </TableCell>
                        <TableCell>
                          {subscription.subscriptionEnd
                            ? format(
                                new Date(subscription.subscriptionEnd),
                                "MMM dd, yyyy"
                              )
                            : "No end date"}
                        </TableCell>
                        <TableCell>
                          {subscription.createdAt
                            ? format(
                                new Date(subscription.createdAt),
                                "MMM dd, yyyy h:mm a"
                              )
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">
                            {subscription.paymentMethod?.name ||
                              "Not specified"}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Pagination Controls */}
            {totalItems > 0 && (
              <div className="flex items-center justify-between px-4 py-4 border-t border-border">
                <div className="text-sm text-muted-foreground">
                  Showing{" "}
                  <span className="font-medium">
                    {totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-medium">
                    {Math.min(currentPage * pageSize, totalItems)}
                  </span>{" "}
                  of <span className="font-medium">{totalItems}</span> filtered
                  subscriptions
                  {totalItems !== subscriptionsOverview?.total && (
                    <span> (total: {subscriptionsOverview?.total || 0})</span>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  {totalPages > 0 && (
                    <div className="flex items-center">
                      <Button
                        className="mr-1 px-2"
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(1)}
                        disabled={!canGoPrev}
                      >
                        First
                      </Button>

                      <Button
                        className="mr-1 px-2"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)
                        }
                        disabled={!canGoPrev}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>

                      {pageNumbers.map((pageNum) => (
                        <Button
                          key={pageNum}
                          className="mx-0.5 w-8 h-8 p-0"
                          variant={
                            pageNum === currentPage ? "default" : "outline"
                          }
                          size="sm"
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      ))}

                      <Button
                        className="ml-1 px-2"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setCurrentPage(
                            currentPage < totalPages
                              ? currentPage + 1
                              : totalPages
                          )
                        }
                        disabled={!canGoNext}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>

                      <Button
                        className="ml-1 px-2"
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={!canGoNext}
                      >
                        Last
                      </Button>
                    </div>
                  )}

                  <select
                    className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1); // Reset to first page when changing page size
                    }}
                  >
                    {[10, 20, 30, 50].map((size) => (
                      <option key={size} value={size}>
                        {size} per page
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default UserSubscriptionsList;
